import { FC, useCallback, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import {
  giftCouponByIdDataAtomFamily,
  giftCouponByIdStatusAtomFamily,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import { requestRefund } from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponByIdHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import { GiftCouponRedeemStatus } from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import BankSelect from '@shared/containers/BankSelect/BankSelect';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { ContactEmailLink } from '../components/ContactEmailLink';
import PhoneVerificationSection from './giftCouponRedeem/PhoneVerificationSection';
import SignatureModal from './giftCouponRedeem/SignatureModal';

interface RefundFormData {
  bank: string;
  accountNumber: string;
  accountHolder: string;
}

const GiftCouponRefundPage: FC = function GiftCouponRefundPage() {
  const { couponId } = useParams<{ couponId: string }>();
  const navigate = useNavigate();

  useGiftCouponByIdHook(couponId || '', !couponId);

  const status = useRecoilValue(giftCouponByIdStatusAtomFamily(couponId || ''));
  const coupon = useRecoilValue(giftCouponByIdDataAtomFamily(couponId || ''));

  const [phoneNumber, setPhoneNumber] = useState('');
  const [requesterName, setRequesterName] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    control,
    register,
    getValues,
    formState: { errors },
    trigger,
  } = useForm<RefundFormData>({
    defaultValues: { bank: '', accountNumber: '', accountHolder: '' },
  });

  const handleSubmitClick = useCallback(async () => {
    if (!requesterName.trim()) {
      setSubmitError('본인 성함을 입력해 주세요.');
      return;
    }
    if (!isVerified) {
      setSubmitError('본인 인증을 완료해 주세요.');
      return;
    }
    const valid = await trigger();
    if (!valid) return;

    const values = getValues();
    if (!values.bank) {
      setSubmitError('환불받으실 은행을 선택해 주세요.');
      return;
    }
    if (!values.accountNumber.trim()) {
      setSubmitError('계좌번호를 입력해 주세요.');
      return;
    }
    if (!values.accountHolder.trim()) {
      setSubmitError('예금주명을 입력해 주세요.');
      return;
    }

    setSubmitError(null);
    setIsSignatureModalOpen(true);
  }, [isVerified, requesterName, trigger, getValues]);

  const handleSignatureConfirm = useCallback(
    async (signatureDataUrl: string) => {
      if (!couponId) return;
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const values = getValues();
        await requestRefund(couponId, {
          phoneNumber: phoneNumber.replace(/-/g, ''),
          name: requesterName.trim(),
          bank: values.bank,
          accountNumber: values.accountNumber.trim(),
          accountHolder: values.accountHolder.trim(),
          signatureImage: signatureDataUrl,
        });
        setSubmitSuccess(true);
        setIsSignatureModalOpen(false);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '환불 신청 중 오류가 발생했습니다.';
        setSubmitError(message);
        setIsSignatureModalOpen(false);
      } finally {
        setIsSubmitting(false);
      }
    },
    [couponId, phoneNumber, requesterName, getValues],
  );

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-50">
        <Loading />
      </div>
    );
  }

  if (!coupon) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6">
        <ExclamationCircleIcon className="h-16 w-16 text-stone-300" />
        <h2 className="text-lg font-bold text-stone-700">쿠폰을 찾을 수 없습니다</h2>
        <p className="text-center text-sm text-stone-500">
          유효하지 않은 링크이거나, 이미 처리된 쿠폰입니다.
        </p>
      </div>
    );
  }

  if (!coupon.issued || coupon.issued.type !== 'refund') {
    const couponCode = coupon.code;
    if (couponCode) {
      navigate(`/gift-store/coupon/${couponCode}`, { replace: true });
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6">
        <ExclamationCircleIcon className="h-16 w-16 text-stone-300" />
        <h2 className="text-lg font-bold text-stone-700">
          환불 신청이 허용되지 않은 쿠폰입니다
        </h2>
        <p className="text-center text-sm text-stone-500">
          관리자가 환불을 허용한 경우에만 이 페이지에 접근할 수 있습니다.
        </p>
      </div>
    );
  }

  if (coupon.issued.requestedAt || coupon.refund?.requestedAt) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6">
        <CheckCircleSolidIcon className="h-16 w-16 text-green-500" />
        <h2 className="text-lg font-bold text-stone-700">환불 신청이 완료되었습니다</h2>
        <p className="text-center text-sm text-stone-500">
          입력하신 계좌로 환불 금액이 입금될 예정입니다.
          <br />
          처리까지 영업일 기준 1~3일 소요됩니다.
        </p>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6">
        <CheckCircleSolidIcon className="h-16 w-16 text-green-500" />
        <h2 className="text-lg font-bold text-stone-700">환불 신청이 완료되었습니다</h2>
        <p className="text-center text-sm text-stone-500">
          입력하신 계좌로 환불 금액이 입금될 예정입니다.
          <br />
          처리까지 영업일 기준 1~3일 소요됩니다.
        </p>
      </div>
    );
  }

  const isDelivered = coupon.redeemStatus === GiftCouponRedeemStatus.Delivered;

  const giftItemObj =
    typeof coupon.giftItem === 'object' &&
    coupon.giftItem &&
    'priceRetail' in coupon.giftItem
      ? (coupon.giftItem as { priceRetail: number; giftItemInfo?: { name?: string } })
      : null;

  const giftItemRetailPrice = giftItemObj?.priceRetail ?? 0;
  const giftItemName = giftItemObj?.giftItemInfo?.name || '답례품';

  const choiceUnit =
    coupon.selectedChoiceUnit &&
    typeof coupon.selectedChoiceUnit === 'object' &&
    'name' in coupon.selectedChoiceUnit
      ? (coupon.selectedChoiceUnit as {
          name: string;
          priceRegular?: number;
          image?: { url: string };
        })
      : null;

  const choiceUnitPrice = choiceUnit?.priceRegular ?? 0;

  const refundInfo = (() => {
    if (!isDelivered) {
      return {
        type: 'expired' as const,
        basePrice: giftItemRetailPrice,
        refundAmount: Math.floor(giftItemRetailPrice * 0.9),
        label: giftItemName,
      };
    }
    if (choiceUnit && choiceUnitPrice > 0 && choiceUnitPrice <= giftItemRetailPrice) {
      return {
        type: 'choiceUnit' as const,
        basePrice: choiceUnitPrice,
        refundAmount: Math.floor(choiceUnitPrice * 0.9),
        label: choiceUnit.name,
      };
    }
    return {
      type: 'giftItem' as const,
      basePrice: giftItemRetailPrice,
      refundAmount: Math.floor(giftItemRetailPrice * 0.9),
      label: giftItemName,
    };
  })();

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-8">
      <div className="mx-auto max-w-md">
        {/* 헤더 */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-800">환불 신청</h1>
          <p className="mt-2 text-sm text-stone-500">
            {isDelivered
              ? '이미 발송 완료된 상품에 대해 환불을 도와드립니다'
              : '답례품 환불을 위해 아래 정보를 입력해 주세요'}
          </p>
        </div>

        {/* 환불 안내 */}
        {isDelivered && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2">
              <ExclamationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="text-xs leading-relaxed text-amber-800">
                <p className="font-bold">꼭 읽어주세요</p>
                <ul className="mt-1.5 list-disc space-y-1 pl-4">
                  <li>
                    이미 <strong>정상 발송 완료</strong>된 상품에 대한 환불입니다.
                  </li>
                  <li>
                    상품 가격의 <strong>90%</strong> 환불이 가능합니다.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {refundInfo.type === 'choiceUnit' || refundInfo.type === 'giftItem' ? (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-stone-700">{refundInfo.label}</p>
              <span className="text-xs text-stone-400">
                {coupon.fullCode || coupon.code}
              </span>
            </div>
            <div className="mt-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-stone-500">상품 가격</span>
                <span className="text-xs text-stone-400 line-through">
                  {refundInfo.basePrice.toLocaleString()}원
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs font-bold text-stone-700">환불 예정 금액</span>
                <span className="text-base font-bold text-stone-800">
                  {refundInfo.refundAmount.toLocaleString()}원
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2">
              <ExclamationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="text-xs leading-relaxed text-amber-800">
                <p className="font-bold">환불 규정 안내</p>
                <p className="mt-1">
                  유효기간 만료 후 환불 규정에 의해 상품 가격의 90%를 환불해 드립니다.
                </p>
                <div className="mt-2 flex items-center justify-between rounded-md bg-white/60 px-3 py-2">
                  <span className="text-stone-600">{refundInfo.label}</span>
                  <span className="font-bold text-stone-800">
                    {refundInfo.refundAmount.toLocaleString()}원
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: 본인 인증 */}
        <PhoneVerificationSection
          couponId={couponId || ''}
          isSelectable={false}
          phoneNumber={phoneNumber}
          onPhoneNumberChange={setPhoneNumber}
          isVerified={isVerified}
          onVerified={() => setIsVerified(true)}
          onVerificationReset={() => setIsVerified(false)}
          submitError={null}
          isSubmitting={isSubmitting}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onSubmit={() => {}}
          hideSubmitButton
          showRequesterName
          requesterName={requesterName}
          onRequesterNameChange={setRequesterName}
          titleText="본인 확인"
          descriptionText="환불 처리를 위해 휴대폰 번호를 입력해 주세요"
          subText="교환 신청 시 인증하셨던 번호로만 환불이 가능합니다"
        />

        {/* STEP 2: 환불 계좌 정보 */}
        {isVerified && (
          <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-center gap-2">
              <span className="rounded-full bg-stone-800 px-2.5 py-0.5 text-base font-bold text-white">
                STEP 2
              </span>
            </div>

            <h3 className="text-center text-lg font-bold text-stone-700">
              환불 계좌 정보
            </h3>
            <p className="mt-1.5 text-center text-sm text-stone-400">
              환불받으실 계좌 정보를 입력해 주세요
            </p>

            {/* 은행 선택 */}
            <div className="mt-5">
              <label className="mb-1.5 block text-sm font-medium text-stone-600">
                은행 선택
              </label>
              <BankSelect placeholder="은행을 선택하세요" control={control} name="bank" />
            </div>

            {/* 계좌번호 */}
            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-medium text-stone-600">
                계좌번호
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="숫자만 입력"
                {...register('accountNumber', { required: '계좌번호를 입력해 주세요.' })}
                className="w-full rounded-lg border border-stone-200 px-4 py-3.5 text-base text-stone-700 placeholder:text-stone-300 focus:border-stone-400 focus:outline-none"
              />
              {errors.accountNumber && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.accountNumber.message}
                </p>
              )}
            </div>

            {/* 예금주 */}
            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-medium text-stone-600">
                예금주명
              </label>
              <input
                type="text"
                placeholder="예금주명 입력"
                {...register('accountHolder', {
                  required: '예금주명을 입력해 주세요.',
                })}
                className="w-full rounded-lg border border-stone-200 px-4 py-3.5 text-base text-stone-700 placeholder:text-stone-300 focus:border-stone-400 focus:outline-none"
              />
              {errors.accountHolder && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.accountHolder.message}
                </p>
              )}
            </div>

            {submitError && (
              <p className="mt-4 text-center text-sm text-red-500">{submitError}</p>
            )}

            {/* 환불 신청 버튼 */}
            <button
              onClick={handleSubmitClick}
              disabled={isSubmitting}
              className={`mt-6 w-full rounded-lg py-4 text-base font-bold transition ${
                isSubmitting
                  ? 'bg-stone-200 text-stone-400'
                  : 'bg-stone-800 text-white active:bg-stone-900'
              }`}
            >
              {isSubmitting ? '처리 중...' : '환불 신청하기'}
            </button>
          </div>
        )}

        {/* 하단 연락처 */}
        <div className="mt-8 text-center">
          <ContactEmailLink />
        </div>
      </div>

      {/* 서명 모달 */}
      <SignatureModal
        open={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        onConfirm={handleSignatureConfirm}
        isSubmitting={isSubmitting}
        title="환불 지급을 위한 서명"
      />
    </div>
  );
};

export default GiftCouponRefundPage;
